import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { useContext } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import AccountLayout from "./AccountLayout";
import { AuthContext } from "../../../Context/AuthContext";
import { STATUS_STEPS, formatShippingMethod, formatNgn, isPayOnDeliveryOrder } from "../libs/orderDisplayUtils";
import OrderPaymentBadge, { canCancelOrder } from "../components/OrderPaymentBadge";
import RetryPaymentButton from "../components/RetryPaymentButton";
import { AccountOrderDetailSkeleton } from "./AccountSkeletons";
import "./UserProfile.css";

const OrderDetail = () => {
  const { orderId } = useParams();
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [returnReason, setReturnReason] = useState("");
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const { data, isLoading, isError } = useQuery(
    ["order", orderId],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    { enabled: !!orderId && !!token }
  );

  const order = data?.order;
  const returnRequest = data?.returnRequest;

  const handleCancel = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/${orderId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order canceled");
      queryClient.invalidateQueries(["order", orderId]);
      queryClient.invalidateQueries("myOrders");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not cancel");
    }
  };

  const handleReturnRequest = async (e) => {
    e.preventDefault();
    const reason = returnReason.trim();
    if (!reason) {
      toast.error("Please describe why you want to return this order");
      return;
    }
    setSubmittingReturn(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/customer/returns`,
        { orderId, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Return request submitted");
      setReturnReason("");
      queryClient.invalidateQueries(["order", orderId]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not submit return request");
    } finally {
      setSubmittingReturn(false);
    }
  };

  const currentStep =
    order?.status === "Canceled" ? -1 : order ? STATUS_STEPS.indexOf(order.status) : -1;

  if (isLoading) {
    return (
      <AccountLayout loading>
        <AccountOrderDetailSkeleton />
      </AccountLayout>
    );
  }

  return (
    <AccountLayout title="Order Details">
      {isError && (
        <p className="account-error">
          Could not load order. <Link to="/account/orders">Back to orders</Link>
        </p>
      )}
      {order && (
        <>
          <div className="order-detail-meta">
            <div className="order-detail-meta__item">
              <span>Order number</span>
              <strong>{order.orderNumber}</strong>
            </div>
            <div className="order-detail-meta__item">
              <span>Date placed</span>
              <strong>{new Date(order.createdAt).toLocaleString()}</strong>
            </div>
            <div className="order-detail-meta__item">
              <span>Status</span>
              <strong>{order.status}</strong>
            </div>
            <div className="order-detail-meta__item">
              <span>Payment</span>
              <OrderPaymentBadge order={order} showDue />
            </div>
            <div className="order-detail-meta__item">
              <span>Shipping</span>
              <strong>
                {formatShippingMethod(order.shippingMethod)} — {formatNgn(order.shippingCost)}
              </strong>
            </div>
            <div className="order-detail-meta__item">
              <span>Total</span>
              <strong>{formatNgn(order.totalAmount)}</strong>
            </div>
          </div>

          {isPayOnDeliveryOrder(order) && order.paymentStatus === "pending" && (
            <div className="order-pod-banner">
              Pay on delivery — have <strong>{formatNgn(order.totalAmount)}</strong> ready when your order arrives.
            </div>
          )}

          {order.status === "Canceled" ? (
            <p className="order-canceled-badge">This order was canceled.</p>
          ) : (
            <div className="order-timeline">
              {STATUS_STEPS.map((step, idx) => (
                <div key={step} className={`timeline-step ${idx <= currentStep ? "active" : ""}`}>
                  <span className="timeline-dot" />
                  <small>{step}</small>
                </div>
              ))}
            </div>
          )}

          <div className="order-detail-section">
            <h4>Items</h4>
            <ul className="order-items-list order-items-detailed">
              {(order.items || []).map((item) => (
                <li key={item._id}>
                  <span>
                    {item.name} x{item.quantity}
                    {item.size ? ` (${item.size})` : ""} —{" "}
                    {formatNgn(item.unitPrice * item.quantity)}
                  </span>
                  <span className="order-item-status-badge">{item.status || order.status}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="order-detail-section">
            <h4>Shipping address</h4>
            <p>{order.shippingAddress?.full_name}</p>
            <p>
              {order.shippingAddress?.address}, {order.shippingAddress?.city},{" "}
              {order.shippingAddress?.state}
            </p>
            <p>{order.shippingAddress?.phone_number}</p>
          </div>

          {order.status === "Delivered" && (
            <div className="order-return-panel">
              <h4>Returns</h4>
              {returnRequest ? (
                <>
                  <p>
                    Return status: <strong>{returnRequest.status}</strong>
                  </p>
                  <p className="dashboard-form-hint">{returnRequest.reason}</p>
                  {returnRequest.adminNote && (
                    <p className="dashboard-form-hint">Note: {returnRequest.adminNote}</p>
                  )}
                </>
              ) : (
                <form className="order-return-form" onSubmit={handleReturnRequest}>
                  <p className="dashboard-form-hint">
                    Request a return for this delivered order. The seller will review your request.
                  </p>
                  <textarea
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder="Why are you returning this order?"
                    maxLength={1000}
                    required
                  />
                  <button type="submit" className="order-action--primary" disabled={submittingReturn}>
                    {submittingReturn ? "Submitting…" : "Request return"}
                  </button>
                </form>
              )}
            </div>
          )}

          <div className="order-card-actions">
            <Link to={`/market/track-order?orderNumber=${order.orderNumber}`}>Track order</Link>
            <Link to="/account/orders">Back to orders</Link>
            <RetryPaymentButton order={order} />
            {canCancelOrder(order) && (
              <button type="button" className="order-action--danger" onClick={handleCancel}>
                Cancel order
              </button>
            )}
          </div>
        </>
      )}
    </AccountLayout>
  );
};

export default OrderDetail;
